import type { BlockFullWidthCta } from 'types/block-full-width-cta';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';

import { Button, Wrap } from '@leafwell/components';

import HeadingBody from './heading-body';
import Figure from './figure';
import { useEventsTracker } from 'contexts/eventsTracker';
import { toQueryString } from 'lib/utils/toQueryString';

const LeafwellLogoSvg: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <span className={className}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1150 420"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.1"
          d="M485.604 659.742V486.952H0V312.812H360.552L34.7299 -7.12027L160.15 -130.332L485.604 189.232V-309.258H663.058V190.46L988.635 -129.35L1114.06 -6.13849L789.46 312.812H1149.28V486.952H663.058V659.742H485.604Z"
          fill="#F5F1FD"
        />
      </svg>
    </span>
  );
};

const FullWidthCta: React.FC<BlockFullWidthCta> = ({
  sectionId,
  instanceId,
  title,
  description,
  button,
  image,
}) => {
  const { trackEvent } = useEventsTracker();

  const { asPath, query } = useRouter();
  const posthog = usePostHog();
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();

  const product: 'membership' | 'telepharmacist' | null = asPath.includes(
    '/membership',
  )
    ? 'membership'
    : asPath.includes('/cannabis-care')
    ? 'telepharmacist'
    : null;
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const queryParams = toQueryString(query);

  useEffect(() => {
    if (product) {
      setNewUrl(
        button?.url?.includes('?')
          ? `${button.url}&session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`
          : `${button.url}?session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`,
      );
    }
  }, [sessionId, distinctId, product, queryParams]);

  return (
    <section
      id={sectionId}
      itemID={`FullWidthCta-${instanceId}`}
      className={[
        'relative',
        image ? 'bg-lilac-50' : 'bg-purple-gradient',
      ].join(' ')}
    >
      {!image ? (
        <LeafwellLogoSvg className="absolute bottom-0 top-0 left-1/2 -translate-x-1/2" />
      ) : null}
      <Wrap
        className={[
          'grid gap-y-6 justify-items-center',
          image ? '!pb-0' : '',
        ].join(' ')}
      >
        {title && (
          <HeadingBody
            level="2"
            tagName="h2"
            title={title}
            className={[
              'font-semibold text-center',
              image ? 'text-black' : 'text-white',
            ].join(' ')}
          />
        )}
        {description && (
          <HeadingBody
            level="6"
            tagName="p"
            title={description}
            className={[
              'text-center lg:max-w-3xl',
              image ? 'text-black' : 'text-white',
            ].join(' ')}
          />
        )}
        {button ? (
          <Button
            label={button.title}
            href={product ? newUrl : button.url}
            rounded={true}
            variant={image ? 'secondary' : 'light'}
            className="w-full lg:w-fit h-fit justify-self-center"
            onClick={() => {
              trackEvent({
                event: 'select_content',
                content_type: button.title,
                item_id: `FullWidthCta-${instanceId}`,
                action: 'FullWidthCta-click-cta',
              });
              if (product) {
                trackEvent({
                  event: 'generate_lead',
                  item_id: `FullWidthCta-${instanceId}`,
                  service: product,
                  page_referrer: document.referrer,
                });
              }
            }}
          />
        ) : null}
      </Wrap>
      {image ? <Figure {...image} classNameImage="mx-auto" /> : null}
    </section>
  );
};

export default FullWidthCta;
